
import React, {useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Text,
    useMultiStyleConfig,
    IconButton,
    Link as ChakraLink,
    Flex,
    Radio,
    RadioGroup,
    Spinner
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {FormattedMessage, useIntl} from 'react-intl'

/* Components */
import Link from '@salesforce/retail-react-app/app/components/link'
import Field from '../../field'

/* Hooks */
import {useForm} from 'react-hook-form'
import {useNewsletter} from '../../../integrations/newsletter/hooks/use-newsletter'
import {useBrand} from '../../../contexts/brand-context'
import {useGlobalNewsletterSubscription} from '../../../contexts/global-elements-context'

/* Icons */
import {RightArrowWhite, ConfirmationIcon} from '../../icons'
import {parseShortcodes} from '../../optimizely/gdi/utils/gdi-shortcode-parser'
import {convertCmsStyles} from '../utils'

const COMPONENT_CONSTANTS = {
    newsletterType: 'Footer',
    newsletterSource: 'WebSite'
}

/* Communication-method values for the SMS/Email radio group. */
const METHODS = {
    SMS: 'sms',
    EMAIL: 'email'
}

const NewsletterSignUp = ({...otherProps}) => {
    const styles = useMultiStyleConfig('Newsletter')
    const intl = useIntl()
    const {isGarage} = useBrand()
    const brandName = isGarage ? 'Garage' : 'Dynamite'
    const {data: newsletterSubscription} = useGlobalNewsletterSubscription()
    const cmsTitleText = newsletterSubscription?.title?.textForWeb
        ? parseShortcodes(newsletterSubscription.title.textForWeb, {brandName})
        : null
    const cmsTitleStyles = convertCmsStyles(newsletterSubscription?.title?.stylesPerViewType)
    const cmsPrivacyText = newsletterSubscription?.privacyText?.textForWeb
        ? parseShortcodes(newsletterSubscription.privacyText.textForWeb, {brandName})
        : null
    const cmsPrivacyStyles = convertCmsStyles(newsletterSubscription?.privacyText?.stylesPerViewType)
    const form = useForm({mode: 'onSubmit'})
    const {setError, control, formState: {errors}} = form
    /* Default to SMS to match the design (selected SMS radio + "Phone Number" placeholder). */
    const [method, setMethod] = useState(METHODS.SMS)

    const handleMethodChange = (val) => {
        setMethod(val)
    }
    const [newsletterData, setNewsletterData] = useState({
        email: null,
        type: null,
        source: null
    })
    const [pendingSubmit, setPendingSubmit] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [submittedWithSuccess, setSubmittedWithSuccess] = useState(false)
    const {submitNewsletter} = useNewsletter({...newsletterData})

    /* submitNewsletter is bound at render time to the current newsletterData.
     * We must wait for the state update (and re-render) before calling it,
     * otherwise it fires with stale null values. */
    useEffect(() => {
        if (!pendingSubmit || !newsletterData.email) return

        const doSubmit = async () => {
            setIsSubmitting(true)
            const response = await submitNewsletter()
            if (response?.success) {
                setError('formError', {type: '', message: ''})
                setSubmittedWithSuccess(true)
            } else {
                setError('formError', {type: 'manual', message: response?.errorMessage})
                setSubmittedWithSuccess(false)
            }
            setIsSubmitting(false)
            setPendingSubmit(false)
        }

        doSubmit()
    }, [pendingSubmit, newsletterData.email]) // eslint-disable-line react-hooks/exhaustive-deps

    const onSubmit = (data) => {
        /* SMS submission is intentionally a no-op for now — the integration will be
         * wired up separately. The existing email flow is preserved unchanged. */
        if (method === METHODS.SMS) {
            console.log('[NewsletterSignUp] SMS submit - phone:', data?.newsletterPhone)
            return
        }

        setNewsletterData({
            email: data?.newsletterEmail,
            type: COMPONENT_CONSTANTS.newsletterType,
            source: COMPONENT_CONSTANTS.newsletterSource
        })
        setPendingSubmit(true)
    }

    const submitAriaLabel = intl.formatMessage({
        defaultMessage: 'Submit',
        id: 'newsletter_cta.label'
    })

    return (
        <Box {...otherProps}>
            {cmsTitleText ? (
                <Box
                    sx={{
                        '& p': {textStyle: 'bodySmRegular'},
                        '& h1': {textStyle: 'webH1'},
                        '& h2': {textStyle: 'webH2'},
                        '& h3': {textStyle: 'webH3'},
                        '& h4, & h5, & h6': {textStyle: 'webH4'},
                        '& a': {textDecoration: 'underline'},
                        '& a:hover': {textDecoration: 'underline'}
                    }}
                    {...cmsTitleStyles}
                    dangerouslySetInnerHTML={{__html: cmsTitleText}}
                />
            ) : (
                <>
                    <Text {...styles.title} {...styles.largeTypo}>
                        <FormattedMessage
                            defaultMessage="Stay in the know"
                            id="footer.subscribe.heading.stay_in_know"
                        />
                    </Text>
                    <Text {...styles.preferenceLabel} {...styles.xsTypo}>
                        <FormattedMessage
                            defaultMessage="Select your preferred communication method"
                            id="newsletter.preference.label"
                        />
                    </Text>
                </>
            )}
            <>
                <RadioGroup
                    value={method}
                    onChange={handleMethodChange}
                    {...styles.methodRadioGroup}
                >
                    <Flex {...styles.methodRadioRow}>
                        <Radio
                            value={METHODS.SMS}
                            size="lg"
                            alignItems="center"
                            data-testid="newsletter-method-sms"
                        >
                            <Text {...styles.nsOption}>
                                <FormattedMessage defaultMessage="SMS" id="newsletter.method.sms" />
                            </Text>
                        </Radio>
                        <Radio
                            value={METHODS.EMAIL}
                            size="lg"
                            alignItems="center"
                            data-testid="newsletter-method-email"
                        >
                            <Text {...styles.nsOption}>
                                <FormattedMessage
                                    defaultMessage="Email"
                                    id="newsletter.method.email"
                                />
                            </Text>
                        </Radio>
                    </Flex>
                </RadioGroup>
                <form onSubmit={form.handleSubmit(onSubmit)} noValidate>
                    <Box {...styles.fieldContainer}>
                        {method === METHODS.EMAIL ? (
                            <Field
                                key="email"
                                name="newsletterEmail"
                                type="email"
                                variant="webFloatingField"
                                control={control}
                                defaultValue=""
                                label={
                                    isGarage
                                        ? intl.formatMessage({
                                              defaultMessage: 'Email Address',
                                              id: 'newsletter_email.label.garage'
                                          })
                                        : intl.formatMessage({
                                              defaultMessage: 'Email Address',
                                              id: 'newsletter_email.label.dynamite'
                                          })
                                }
                                rules={{
                                    required: isGarage
                                        ? intl.formatMessage({
                                              defaultMessage: 'Please enter a valid email address',
                                              id: 'newsletter.field.error.email.garage'
                                          })
                                        : intl.formatMessage({
                                              defaultMessage: 'Please enter a valid email address',
                                              id: 'newsletter.field.error.email.dynamite'
                                          }),
                                    pattern: {
                                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                        message: isGarage
                                            ? intl.formatMessage({
                                                  defaultMessage:
                                                      'Please enter a valid email address',
                                                  id: 'newsletter.field.error.email.garage'
                                              })
                                            : intl.formatMessage({
                                                  defaultMessage:
                                                      'Please enter a valid email address',
                                                  id: 'newsletter.field.error.email.dynamite'
                                              })
                                    }
                                }}
                                error={errors?.newsletterEmail}
                                formControlProps={{isDisabled: isSubmitting}}
                                webHelperText={
                                    submittedWithSuccess
                                        ? isGarage
                                            ? intl.formatMessage({
                                                  defaultMessage: 'Thanks for subscribing',
                                                  id: 'newsletter_confirmation.message.garage'
                                              })
                                            : intl.formatMessage({
                                                  defaultMessage: 'Thanks for subscribing',
                                                  id: 'newsletter_confirmation.message.dynamite'
                                              })
                                        : undefined
                                }
                                webHelperIcon={submittedWithSuccess ? ConfirmationIcon : undefined}
                                inputProps={({onChange}) => ({
                                    'data-testid': 'email-input',
                                    paddingRight: '56px',
                                    onChange: (e) => {
                                        onChange(e)
                                        form.clearErrors('formError')
                                        if (!e.target.value) {
                                            form.clearErrors('newsletterEmail')
                                            setSubmittedWithSuccess(false)
                                        }
                                    }
                                })}
                            />
                        ) : (
                            <Field
                                key="phone"
                                name="newsletterPhone"
                                type="tel"
                                variant="webFloatingField"
                                control={control}
                                defaultValue=""
                                label={intl.formatMessage({
                                    defaultMessage: 'Phone Number',
                                    id: 'newsletter_phone.label'
                                })}
                                rules={{
                                    required: intl.formatMessage({
                                        defaultMessage: 'Please enter a valid phone number',
                                        id: 'newsletter.field.error.phone'
                                    })
                                }}
                                error={errors?.newsletterPhone}
                                formControlProps={{isDisabled: isSubmitting}}
                                inputProps={() => ({
                                    'data-testid': 'phone-input',
                                    paddingRight: '56px'
                                })}
                            />
                        )}
                        <IconButton
                            type="submit"
                            title={submitAriaLabel}
                            aria-label={submitAriaLabel}
                            data-testid="newsletter-submit-button"
                            icon={
                                isSubmitting ? (
                                    <Spinner mt={'5px'} size="sm" color="white" />
                                ) : (
                                    <RightArrowWhite boxSize="24px" />
                                )
                            }
                            pointerEvents={isSubmitting ? 'none' : 'auto'}
                            width="48px"
                            height="48px"
                            minW="48px"
                            borderRadius={0}
                            bg="webButton.btnPrimaryDefault"
                            color="webTypo.textPrimaryReverse"
                            _hover={{bg: 'webButton.btnPrimaryHover'}}
                            _active={{bg: 'webButton.btnPrimaryHover'}}
                            {...styles.submitButton}
                            top="0"
                        />

                        {errors.formError && (
                            <Text {...styles.fieldError}>{errors.formError.message}</Text>
                        )}
                    </Box>
                </form>

                {cmsPrivacyText ? (
                    <Box
                        {...styles.disclaimerText}
                        sx={{
                            '& p': {textStyle: 'bodySmRegular'},
                            '& a': {textDecoration: 'underline'},
                            '& a:hover': {textDecoration: 'underline'}
                        }}
                        {...cmsPrivacyStyles}
                        dangerouslySetInnerHTML={{__html: cmsPrivacyText}}
                    />
                ) : (
                    <Text {...styles.disclaimerText}>
                        <FormattedMessage
                            id="newsletter_disclaimer.text"
                            defaultMessage="By signing up for SMS, you agree to receive promotional text messages from {brandName}. Message and data rates may apply. Message frequency varies. Reply STOP to unsubscribe. See <policy>Privacy Policy</policy> and <terms>Terms</terms>."
                            values={{
                                brandName,
                                policy: (chunks) => (
                                    <ChakraLink
                                        as={Link}
                                        textDecoration="underline"
                                        to="/privacy-policy.html"
                                        data-testid="privacy-policy-link"
                                    >
                                        {chunks}
                                    </ChakraLink>
                                ),
                                terms: (chunks) => (
                                    <ChakraLink
                                        as={Link}
                                        textDecoration="underline"
                                        to="/terms-conditions.html"
                                        data-testid="terms-link"
                                    >
                                        {chunks}
                                    </ChakraLink>
                                )
                            }}
                        />
                    </Text>
                )}
            </>
        </Box>
    )
}

NewsletterSignUp.propTypes = {
    otherProps: PropTypes.object
}

export default NewsletterSignUp
